import useAuth from '../hooks/useAuth';

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FirebaseAuthService } from '../services/FirebaseAuthService';
import { Box, Typography, Container, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { GoogleSheetsService, Show } from '../services/GoogleSheetsService';

// Helper to parse and normalize added money input
function getAddedMoney(value: any): number {
  if (!value) {
    return 0;
  }
  if (typeof value === 'number') {
    return value > 0 ? value : 0;
  }
  if (typeof value === 'string') {
    // Remove $ and whitespace, parse as int
    const cleaned = value.replace(/[^\d]/g, '');
    const num = parseInt(cleaned, 10);
    return isNaN(num) ? 0 : num;
  }
  return 0;
}

const ShowManagementPage: React.FC = () => {
  const [shows, setShows] = useState<Show[]>([]);
  const [addMode, setAddMode] = useState(false);
  const [selectedTemplateShowName, setSelectedTemplateShowName] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedShow, setSelectedShow] = useState<Show | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  // Editable fields
  const [editShow, setEditShow] = useState<Show | null>(null);
  const [editDate, setEditDate] = useState<string>('');
  const [editTime, setEditTime] = useState<string>('14:00'); // default to 2:00 PM
  const [saving, setSaving] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
  const user = useAuth();
  const navigate = useNavigate();

    // Refresh handler
    const handleRefresh = async () => {
      setLoading(true);
      setError(null);
      try {
         if (!user.user) throw new Error('User not authenticated');
         const service = await GoogleSheetsService.fromFirebaseUser(user.user);
        if (!service) throw new Error('Failed to initialize Google Sheets service');
        const records: Show[] = await service.getAllShows();
        setShows(records.filter(s => s.showName.trim() !== '' && s.showDateTime.trim() !== ''));
      } catch (err) {
        setError('Failed to refresh shows.');
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    const fetchShows = async () => {
      setLoading(true);
      setError(null);
      // Check for access token
      if (!FirebaseAuthService.getAccessToken()) {
        navigate('/admin');
        return;
      }
      try {
        if (!user) {
          setError('User not authenticated.');
          setLoading(false);
          return;
        }
         if (!user.user) throw new Error('User not authenticated');
         const service = await GoogleSheetsService.fromFirebaseUser(user.user);
        if (!service) {
          setError('Failed to initialize Google Sheets service.');
          setLoading(false);
          return;
        }
        const records: Show[] = await service.getAllShows();
        setShows(records.filter(s => s.showName.trim() !== '' && s.showDateTime.trim() !== ''));
      } catch (err) {
        setError('Failed to load shows.');
      } finally {
        setLoading(false);
      }
    };
    fetchShows();
  }, [user, navigate]);

  const handleRowClick = (show: Show) => {
    setSelectedShow(show);
    setEditShow({ ...show });
    setAddMode(false);
    setModalOpen(true);
    // Parse ISO date/time for UI fields
    let datePart = '', timePart = '';
    if (show.showDateTime) {
      // Try to parse as ISO, fallback to legacy format
      const d = new Date(show.showDateTime);
      if (!isNaN(d.getTime())) {
        // Use local date and time for input fields
        // Use local time, not UTC, for user input
        const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
        datePart = local.toISOString().slice(0, 10); // YYYY-MM-DD
        timePart = local.toISOString().slice(11, 16); // HH:mm
      } else if (/\d{1,2}\/\d{1,2}\/\d{4}/.test(show.showDateTime)) {
        // Legacy format: MM/DD/YYYY HH:mm
        const [date, time] = show.showDateTime.split(' ');
        datePart = date;
        timePart = time || '';
      }
    }
    setEditDate(datePart);
    setEditTime(timePart);
  };

  // Add Show button handler
  const handleAddShowClick = () => {
    setAddMode(true);
    setSelectedShow(null);
    setEditShow({
      showName: '',
      streetAddress: '',
      cityState: '',
      addedMoney: '',
      showDetails: `START TIMES: 2pm\nOpen arena 1pm\nGate fee $5 per person (8 & under FREE)\nClasses offered and order of events...\nPee Wee Barrels $5\nYouth Barrels (15 & under) EF: $30\nOpen 4D Barrels EF: $40\nExhibitions: $5 each\nOffice Fee: $5 per entry (except pee wee & exhibitions)\nALL riders 12 & under must wear helmet\nPermits for non-members $10`,
      showDateTime: '',
      canceled: false,
      rowIndexId: 0,
      showId: crypto.randomUUID(),
    });
    setEditDate('');
    setEditTime('');
    setSelectedTemplateShowName('');
    setModalOpen(true);
  };

  const handleModalClose = () => {
  setModalOpen(false);
  setSelectedShow(null);
  setEditShow(null);
  setEditDate('');
  setEditTime('');
  };

  // Logout handler
  const handleLogout = () => {
    FirebaseAuthService.clearCredentials();
    navigate('/admin');
  };

  return (
    <Container maxWidth="md">
      <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mt: 2, mb: 2 }}>
        Back
      </Button>
      <Paper elevation={3} sx={{ padding: 4, marginTop: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2">Show Management</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <Button variant="contained" color="primary" onClick={handleAddShowClick} disabled={loading}>
              Add Show
            </Button>
            <Button variant="outlined" onClick={handleRefresh} disabled={loading}>
              Refresh
            </Button>
          </Box>
        </Box>
        {error && <Typography color="error">{error}</Typography>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <Typography>Loading...</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid #e0e0e0', padding: 8, background: '#4d4d4dff', width: '1%', whiteSpace: 'nowrap' }}>Date/Time</th>
                  <th style={{ border: '1px solid #e0e0e0', padding: 8, background: '#4d4d4dff' }}>Show Name</th>
                </tr>
              </thead>
              <tbody>
                {shows.map((show, idx) => (
                  <tr key={idx} style={{ cursor: 'pointer' }} onClick={() => handleRowClick(show)}>
                    <td style={{ border: '1px solid #e0e0e0', padding: 8, width: '1%', whiteSpace: 'nowrap' }}>
                      <div>{(() => {
                        if (!show.showDateTime) return '';
                        const d = new Date(show.showDateTime);
                        if (!isNaN(d.getTime())) {
                          return d.toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                        }
                        // fallback for legacy format
                        return show.showDateTime;
                      })()}</div>
                      {show.canceled && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
                          <span className="show-cancelled-chip">
                            <span style={{ color: '#d32f2f', border: '1px solid #d32f2f', borderRadius: '16px', padding: '2px 12px', fontWeight: 500, background: 'transparent', fontSize: '0.95em', display: 'inline-block' }}>Show Canceled</span>
                          </span>
                        </div>
                      )}
                      {!show.canceled && getAddedMoney(show.addedMoney) > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
                          <span className="show-added-money-chip">
                            <span style={{ color: '#388e3c', border: '1px solid #388e3c', borderRadius: '16px', padding: '2px 12px', fontWeight: 500, background: 'transparent', fontSize: '0.95em', display: 'inline-block' }}>
                              ${getAddedMoney(show.addedMoney)}
                            </span>
                          </span>
                        </div>
                      )}
                    </td>
                    <td style={{ border: '1px solid #e0e0e0', padding: 8 }}>{show.showName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {shows.length === 0 && (
              <Typography variant="body2" sx={{ mt: 2 }}>No shows found.</Typography>
            )}
          </Box>
        )}

        <Dialog open={modalOpen} onClose={handleModalClose} maxWidth="sm" fullWidth>
          <DialogTitle>{addMode ? 'Add Show' : 'Edit Show'}</DialogTitle>
          <DialogContent>
            {/* Dropdown for selecting template show */}
            {addMode && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Prepopulate from existing show:</Typography>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="template-show-label">Select Show Name</InputLabel>
                  <Select
                    labelId="template-show-label"
                    value={selectedTemplateShowName}
                    label="Select Show Name"
                    onChange={e => {
                      const name = e.target.value;
                      setSelectedTemplateShowName(name);
                      // Find first show with this name
                      const template = shows.find(s => s.showName === name);
                      if (template) {
                        setEditShow({
                          ...editShow,
                          showName: template.showName,
                          streetAddress: template.streetAddress,
                          cityState: template.cityState,
                          showDetails: template.showDetails,
                          addedMoney: template.addedMoney,
                          canceled: false,
                          rowIndexId: 0,
                          showDateTime: template.showDateTime || '',
                          showId: crypto.randomUUID(),
                        });
                        // Also set editDate and editTime from template.showDateTime
                        let datePart = '', timePart = '';
                        if (template.showDateTime) {
                          const d = new Date(template.showDateTime);
                          if (!isNaN(d.getTime())) {
                            const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
                            datePart = local.toISOString().slice(0, 10); // YYYY-MM-DD
                            timePart = local.toISOString().slice(11, 16); // HH:mm
                          } else if (/\d{1,2}\/\d{1,2}\/\d{4}/.test(template.showDateTime)) {
                            const [date, time] = template.showDateTime.split(' ');
                            datePart = date;
                            timePart = time || '';
                          }
                        }
                        setEditDate(datePart);
                        setEditTime(timePart);
                      }
                    }}
                  >
                    <MenuItem value="">-- Select --</MenuItem>
                    {[...new Set(shows.map(s => s.showName))].map(name => (
                      <MenuItem key={name} value={name}>{name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
            {editShow && (
              <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField
                  label="Show Name"
                  value={editShow.showName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditShow({ ...editShow, showName: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Street Address"
                  value={editShow.streetAddress}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditShow({ ...editShow, streetAddress: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="City/State"
                  value={editShow.cityState}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditShow({ ...editShow, cityState: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Added Money"
                  value={editShow.addedMoney}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    // Normalize and store as $X format
                    let val = e.target.value;
                    // Remove all non-digit characters
                    const cleaned = val.replace(/[^\d]/g, '');
                    const num = parseInt(cleaned, 10);
                    setEditShow({ ...editShow, addedMoney: num > 0 ? `$${num}` : '' });
                  }}
                  fullWidth
                />
                <TextField
                  label="Show Details"
                  value={editShow.showDetails}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditShow({ ...editShow, showDetails: e.target.value })}
                  multiline
                  minRows={3}
                  fullWidth
                />
                <TextField
                  label="Date"
                  type="date"
                  value={(() => {
                    // Convert editDate to YYYY-MM-DD for input
                    let year = '', mm = '', dd = '';
                    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(editDate)) {
                      [mm, dd, year] = editDate.split('/');
                    } else if (/^\d{4}-\d{2}-\d{2}$/.test(editDate)) {
                      return editDate;
                    }
                    return year && mm && dd ? `${year}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}` : '';
                  })()}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    // Convert YYYY-MM-DD to MM/DD/YYYY
                    const val = e.target.value;
                    if (val) {
                      const [year, mm, dd] = val.split('-');
                      setEditDate(`${mm}/${dd}/${year}`);
                    } else {
                      setEditDate('');
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <LocalizationProvider dateAdapter={AdapterDateFns as any}>
                  <TimePicker
                    value={editTime ? new Date(`1970-01-01T${editTime}`) : new Date('1970-01-01T14:00')}
                    onChange={(val: Date | null) => {
                      if (val instanceof Date && !isNaN(val.getTime())) {
                        // Format as HH:mm
                        const hours = val.getHours().toString().padStart(2, '0');
                        const minutes = val.getMinutes().toString().padStart(2, '0');
                        setEditTime(`${hours}:${minutes}`);
                      }
                    }}
                    ampm
                    minutesStep={5}
                    slotProps={{ textField: { fullWidth: true, label: 'Time' } }}
                  />
                </LocalizationProvider>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>Canceled:</Typography>
                  <Button
                    variant={editShow.canceled ? 'contained' : 'outlined'}
                    color={editShow.canceled ? 'error' : 'primary'}
                    onClick={() => setEditShow({ ...editShow, canceled: !editShow.canceled })}
                  >{editShow.canceled ? 'Yes' : 'No'}</Button>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleModalClose}>Cancel</Button>
            {!addMode && (
              <Button color="error" variant="outlined" onClick={() => setDeleteConfirmOpen(true)} disabled={saving || deleting}>Delete</Button>
            )}
            <Button variant="contained" onClick={async () => {
              if (!editShow) return;
              setSaving(true);
              try {
                 if (!user.user) throw new Error('User not authenticated');
                 const service = await GoogleSheetsService.fromFirebaseUser(user.user);
                if (!service) throw new Error('Failed to initialize Google Sheets service');
                // Combine date and time for saving as ISO string
                let isoDateTime = '';
                if (editDate) {
                  // Always save as ISO string (local time)
                  const dateStr = editDate.length === 10 ? editDate : '';
                  const timeStr = editTime && editTime.length === 5 ? editTime : '00:00';
                  // Construct ISO string in local time
                  const localDate = new Date(`${dateStr}T${timeStr}`);
                  if (!isNaN(localDate.getTime())) {
                    isoDateTime = localDate.toISOString();
                  } else {
                    isoDateTime = `${dateStr}T${timeStr}`;
                  }
                }
                if (addMode) {
                  // Insert new show at correct index
                  const newShow = { ...editShow, showDateTime: isoDateTime || '', showId: editShow.showId || crypto.randomUUID() };
                  // Find correct index (after header)
                  const allShows = [...shows, newShow];
                  allShows.sort((a, b) => {
                    const dateA = new Date(a.showDateTime);
                    const dateB = new Date(b.showDateTime);
                    return dateA.getTime() - dateB.getTime();
                  });
                  const insertIndex = allShows.findIndex(s => s === newShow) + 1; // +1 for header row
                  await service.insertShowRowAtIndex(newShow, insertIndex);
                } else {
                  await service.updateShowRowByIndex(editShow.rowIndexId, { ...editShow, showDateTime: isoDateTime || '', showId: editShow.showId });
                }
                // Refresh shows
                const records: Show[] = await service.getAllShows();
                // Sort by date and time
                records.sort((a, b) => {
                  const dateA = new Date(a.showDateTime);
                  const dateB = new Date(b.showDateTime);
                  return dateA.getTime() - dateB.getTime();
                });
                setShows(records.filter(s => s.showName.trim() !== '' && s.showDateTime.trim() !== ''));
                setModalOpen(false);
                setSelectedShow(null);
                setEditShow(null);
                setEditDate('');
                setEditTime('');
                setAddMode(false);
              } catch (err) {
                setError('Failed to save show.');
              } finally {
                setSaving(false);
              }
            }} disabled={saving}>Save</Button>
          </DialogActions>
          {/* Delete Confirmation Modal */}
          <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
            <DialogTitle>Delete Show</DialogTitle>
            <DialogContent>
              <Typography>Are you sure you want to delete this show? This action cannot be undone.</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>Cancel</Button>
              <Button color="error" variant="contained" onClick={async () => {
                if (!editShow) return;
                setDeleting(true);
                try {
                   if (!user.user) throw new Error('User not authenticated');
                   const service = await GoogleSheetsService.fromFirebaseUser(user.user);
                  if (!service) throw new Error('Failed to initialize Google Sheets service');
                  await service.deleteRowByIndex('Shows', editShow.rowIndexId);
                  // Refresh shows
                  const records: Show[] = await service.getAllShows();
                  setShows(records.filter(s => s.showName.trim() !== '' && s.showDateTime.trim() !== ''));
                  setModalOpen(false);
                  setSelectedShow(null);
                  setEditShow(null);
                  setEditDate('');
                  setEditTime('');
                  setDeleteConfirmOpen(false);
                } catch (err) {
                  setError('Failed to delete show.');
                } finally {
                  setDeleting(false);
                }
              }} disabled={deleting}>Delete</Button>
            </DialogActions>
          </Dialog>
        </Dialog>
      </Paper>
    </Container>

  );
};


export default ShowManagementPage;